import { useAppNavigation } from '@/domains/navigation/use-app-navigation';
import { cn } from '@/utils/shad';
import { Button } from './primitives/button';
import { PropsWithChildren, useCallback } from 'react';
import { PARAMS_KEY_SELECTED_PROJECT_ID } from '@/constants/route-params-keys';
import { useProjectsContext } from '@/providers/projects-provider';
import { queryStateSerializer } from '@/utils/useQueryParamState/utils';
import { useEnvironmentContext } from '@/providers/environment.provider';

type EmptyStateProps = {
  title: string;
  description?: string;
  className?: string;
  titleClassName?: string;
  projectsCta?: boolean;
  buttonComponent?: React.ReactNode;
};

export const EmptyState = ({
  title,
  description,
  titleClassName,
  className,
  projectsCta,
  buttonComponent,
}: EmptyStateProps) => {
  const { deploymentMode } = useEnvironmentContext();
  const hoursToProcess = deploymentMode === 'self-hosted' ? '24' : '2';
  return (
    <div className={cn('my-6 flex max-w-137.5 items-center justify-between px-8', className)}>
      <div>
        <div className="mb-4">{buttonComponent}</div>
        <p className={cn('mb-2 text-[16px] font-medium', titleClassName)}>{title}</p>
        <p className="text-darkgrey">
          {description
            ? description
            : `Your interactions are currently been processed. Insights will be visible on the platform within ${hoursToProcess} hours of data collection.`}
        </p>
      </div>

      {projectsCta && <ProjectsCtaButton className="" />}
    </div>
  );
};

type WithEmptyStateProps = PropsWithChildren &
  EmptyStateProps & {
    isEmpty: boolean;
  };

export function WithEmptyState({ isEmpty, children, ...restProps }: WithEmptyStateProps) {
  return <>{isEmpty ? <EmptyState {...restProps} /> : children}</>;
}

export const EmptyStateCard = (props: { subtitle: string; description?: string; title: React.ReactNode }) => {
  return (
    <div className="top-0 right-0 left-0 z-10 flex items-center justify-center py-5 backdrop-blur-[2px]">
      <div className="w-125 rounded-base border bg-background">
        <div className="flex h-12 items-center border-b px-6 text-[16px]">{props.title}</div>
        <div className="py-0">
          <EmptyState
            className="px-6"
            title={props.subtitle || 'Calculating user intents...'}
            description={props.description}
          />
        </div>
      </div>
    </div>
  );
};

const ProjectsCtaButton = ({ className }: { className?: string }) => {
  const { navigateApp } = useAppNavigation();

  const { selectedProject } = useProjectsContext();
  const goToApiKeys = useCallback(() => {
    navigateApp({
      route: '/settings',
      params: {
        settingsTab: queryStateSerializer('project', 'string'),
        [PARAMS_KEY_SELECTED_PROJECT_ID]: queryStateSerializer(selectedProject?.id, 'string'),
      },
    });
  }, [navigateApp, selectedProject?.id]);

  return (
    <Button variant="purple" className={className} onClick={goToApiKeys}>
      Obtain API Key
    </Button>
  );
};
